/**
 * OpportunityCalculatorPageV3 — AIP-internal route at /#/calculator-v3.
 *
 * v2.3: 40 main gate-check questions across 10 checkpoints.
 * Branching follow-up questions fire based on answer point value.
 * Full 40-main + follow-up gate-check + Quick Score 7-question mode.
 * No NDA wall — every AIP user is already inside the platform.
 *
 * Palette: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7
 * No emojis. M-PACT with hyphen.
 */
import { useEffect } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import SecondaryNav, { type SecondaryNavRole } from "@/components/dashboards/SecondaryNav";
import OpportunityCalculatorV3 from "@/components/calculator/OpportunityCalculatorV3";
import { getSession } from "@/lib/session";

function roleForNav(): SecondaryNavRole {
  const r = getSession()?.role;
  if (r === "manager") return "management";
  if (r === "executive" || r === "marketing" || r === "ae" || r === "bdr" || r === "se") {
    return r;
  }
  return "all";
}

export default function OpportunityCalculatorPageV3() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Opportunity Checkpoint Calculator v2.3 — Attilius";
    return () => { document.title = prev; };
  }, []);

  return (
    <OperatorShell breadcrumbs={[{ label: "Opportunity Calculator v2.3" }]} pageTitle="Opportunity Calculator v2.3" pageMeta="Deal calculator preview">
      <SecondaryNav role={roleForNav()} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <header className="mb-8">
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.32em",
              color: "#DC2626",
              textTransform: "uppercase",
              marginBottom: 12,
            }}
          >
            Checkpoint calculator · v2.3 Preview · Manufacturing pilot
          </p>
          <h1
            style={{
              fontSize: 36,
              fontWeight: 800,
              color: "#0B0B0F",
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              marginBottom: 12,
            }}
          >
            Opportunity Calculator
          </h1>
          <p
            style={{
              fontSize: 15,
              color: "#525258",
              lineHeight: 1.6,
              maxWidth: 760,
            }}
          >
            40 main checkpoint questions across 10 checkpoints. Branching follow-up
            questions fire automatically when an answer signals a gap or needs
            verification. Quick Score is 7 questions with no follow-ups — use it for a
            directional read before a call. Five-point graded scale — skip any checkpoint you
            cannot answer yet. Composite 0-100, per-section breakdown with main count
            and follow-up count, and a plain go / no-go verdict. Manufacturing pilot:
            industrial, automotive, aerospace, building materials, industrial equipment,
            contract manufacturing.
          </p>
          <div style={{ marginTop: 16, padding: "10px 16px", background: "#FEF3C7", border: "1px solid #EAB308", borderRadius: 8, display: "inline-block" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#B45309", letterSpacing: "0.04em" }}>
              PREVIEW — v2.3 not yet in production. Production remains v2.2 (22 questions) until Bryan reviews and confirms.
            </span>
          </div>
        </header>

        <OpportunityCalculatorV3 inAip />
      </main>
    </OperatorShell>
  );
}
